import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { ScrollArea } from './ui/scroll-area';
import { Separator } from './ui/separator';
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from './ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select';
import {
  GitBranch, Plus, Loader2, CheckCircle2, Clock, AlertTriangle, ArrowRight, Trash2,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const STATUS_META = {
  not_started: { label: 'Not Started', color: 'bg-slate-100 text-slate-600', icon: Clock },
  scheduled: { label: 'Scheduled', color: 'bg-blue-100 text-blue-700', icon: Clock },
  in_progress: { label: 'In Progress', color: 'bg-amber-100 text-amber-700', icon: ArrowRight },
  migrated: { label: 'Migrated', color: 'bg-emerald-100 text-emerald-700', icon: CheckCircle2 },
  verified: { label: 'Verified', color: 'bg-green-100 text-green-800', icon: CheckCircle2 },
};

export default function ChangeManagement({ token }) {
  const [rollouts, setRollouts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState({ name: '', description: '', platform_type_id: '', target_start: '', target_end: '', property_ids: [] });
  const [properties, setProperties] = useState([]);
  const [platforms, setPlatforms] = useState([]);
  const [saving, setSaving] = useState(false);
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const fetch = useCallback(async () => {
    try {
      const [r, p, pl] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/rollouts`, { headers }),
        axios.get(`${BACKEND_URL}/api/properties`, { headers }),
        axios.get(`${BACKEND_URL}/api/platform-types`, { headers }),
      ]);
      setRollouts(r.data.rollouts || []);
      setProperties(p.data || []);
      setPlatforms(pl.data || []);
    } catch { toast.error('Failed to load'); }
    finally { setLoading(false); }
  }, [headers]);

  useEffect(() => { fetch(); }, [fetch]);

  const create = async () => {
    setSaving(true);
    try {
      await axios.post(`${BACKEND_URL}/api/admin/rollouts`, form, { headers });
      toast.success('Rollout created');
      setCreateOpen(false);
      fetch();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setSaving(false); }
  };

  const updatePropStatus = async (rolloutId, propertyId, status) => {
    try {
      await axios.put(`${BACKEND_URL}/api/admin/rollouts/property-status`, {
        rollout_id: rolloutId, property_id: propertyId, status,
      }, { headers });
      toast.success('Status updated');
      fetch();
    } catch { toast.error('Failed to update'); }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>;

  return (
    <div className="flex h-full" data-testid="change-management">
      <div className="w-72 border-r bg-muted/20 flex flex-col shrink-0">
        <div className="p-4 border-b">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold">Rollouts</h3>
            <Button variant="outline" size="sm" onClick={() => setCreateOpen(true)} className="h-7 gap-1 text-xs" data-testid="create-rollout-btn"><Plus className="h-3 w-3" /> New</Button>
          </div>
        </div>
        <ScrollArea className="flex-1">
          <div className="p-2 space-y-1">
            {rollouts.map(r => (
              <button key={r.id} onClick={() => setSelected(r)} data-testid={`rollout-${r.id}`}
                className={`w-full text-left p-3 rounded-lg transition-colors text-sm ${selected?.id === r.id ? 'bg-primary/10 border border-primary/20' : 'hover:bg-muted/60'}`}>
                <div className="font-medium truncate">{r.name}</div>
                <div className="flex items-center gap-2 mt-1">
                  <Badge variant="outline" className="text-[9px]">{r.status || 'planning'}</Badge>
                  <span className="text-[10px] text-muted-foreground">{r.completed}/{r.total_properties} done</span>
                </div>
                {r.completion_pct > 0 && (
                  <div className="w-full h-1 rounded-full bg-muted mt-1.5 overflow-hidden">
                    <div className="h-full rounded-full bg-emerald-500" style={{ width: `${r.completion_pct}%` }} />
                  </div>
                )}
              </button>
            ))}
            {rollouts.length === 0 && <p className="text-xs text-muted-foreground text-center py-6">No rollouts yet</p>}
          </div>
        </ScrollArea>
      </div>

      <div className="flex-1 overflow-auto">
        {selected ? (
          <div className="p-6 space-y-6 max-w-4xl">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-lg font-bold" style={{ fontFamily: 'Space Grotesk' }}>{selected.name}</h2>
                <p className="text-sm text-muted-foreground">{selected.description}</p>
                <div className="flex gap-2 mt-2">
                  {selected.platform_name && <Badge variant="outline">{selected.platform_name}</Badge>}
                  {selected.from_product_name && <span className="text-xs text-muted-foreground">From: {selected.from_product_name}</span>}
                  {selected.to_product_name && <span className="text-xs">To: <strong>{selected.to_product_name}</strong></span>}
                </div>
              </div>
              <div className="text-right">
                <div className="text-3xl font-bold" style={{ fontFamily: 'Space Grotesk', color: selected.completion_pct === 100 ? '#16a34a' : '#0055FF' }}>{selected.completion_pct}%</div>
                <p className="text-xs text-muted-foreground">{selected.completed} of {selected.total_properties} complete</p>
              </div>
            </div>

            <div className="border rounded-lg overflow-hidden">
              <table className="w-full text-sm">
                <thead><tr className="bg-muted/50">
                  <th className="p-2.5 text-left text-xs font-semibold">Property</th>
                  <th className="p-2.5 text-center text-xs font-semibold">Status</th>
                  <th className="p-2.5 text-center text-xs font-semibold">Scheduled</th>
                  <th className="p-2.5 text-center text-xs font-semibold">Go-Live</th>
                  <th className="p-2.5 text-left text-xs font-semibold">Notes</th>
                </tr></thead>
                <tbody>
                  {(selected.properties || []).map((p, i) => {
                    const meta = STATUS_META[p.status] || STATUS_META.not_started;
                    return (
                      <tr key={i} className="border-t hover:bg-muted/20">
                        <td className="p-2.5 font-medium">{p.property_name || p.property_id}</td>
                        <td className="p-2.5 text-center">
                          <Select value={p.status} onValueChange={(v) => updatePropStatus(selected.id, p.property_id, v)}>
                            <SelectTrigger className="h-7 text-[10px] w-28 mx-auto"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              {Object.entries(STATUS_META).map(([k, v]) => (
                                <SelectItem key={k} value={k}><span className="text-xs">{v.label}</span></SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </td>
                        <td className="p-2.5 text-center text-xs text-muted-foreground">{p.scheduled_date || '-'}</td>
                        <td className="p-2.5 text-center text-xs text-muted-foreground">{p.go_live_date || '-'}</td>
                        <td className="p-2.5 text-xs text-muted-foreground">{p.notes || '-'}{p.blockers && <Badge className="ml-1 text-[8px] bg-red-100 text-red-700">Blocker: {p.blockers}</Badge>}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          <div className="h-full flex items-center justify-center text-muted-foreground">
            <div className="text-center"><GitBranch className="h-10 w-10 mx-auto mb-3 opacity-30" /><p className="text-sm">Select a rollout to manage</p></div>
          </div>
        )}
      </div>

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Create Rollout</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <div><label className="text-sm font-medium">Name</label><Input value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} placeholder="Migrate PMS to RoomMaster" className="mt-1" data-testid="rollout-name" /></div>
            <div><label className="text-sm font-medium">Description</label><Input value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} placeholder="Phase 1: Hilton properties" className="mt-1" /></div>
            <div><label className="text-sm font-medium">Platform</label>
              <Select value={form.platform_type_id} onValueChange={v => setForm(p => ({ ...p, platform_type_id: v }))}>
                <SelectTrigger className="mt-1"><SelectValue placeholder="Select platform..." /></SelectTrigger>
                <SelectContent>{platforms.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><label className="text-sm font-medium">Properties ({form.property_ids.length} selected)</label>
              <div className="mt-1 max-h-[150px] overflow-y-auto border rounded-lg p-2 space-y-1">
                {properties.filter(p => p.id !== 'corporate').map(p => (
                  <label key={p.id} className="flex items-center gap-2 text-sm cursor-pointer hover:bg-muted/40 p-1 rounded">
                    <input type="checkbox" checked={form.property_ids.includes(p.id)} onChange={() => setForm(prev => ({ ...prev, property_ids: prev.property_ids.includes(p.id) ? prev.property_ids.filter(x => x !== p.id) : [...prev.property_ids, p.id] }))} className="rounded" />
                    {p.name}
                  </label>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)}>Cancel</Button>
            <Button onClick={create} disabled={saving || !form.name || form.property_ids.length === 0}>{saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}Create Rollout</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
